// Shared, session-scoped camera stream + continuous recorder singleton.
// Lets continuous video recording start the moment camera permission is granted
// (during the system check) and persist as ONE recording through identity
// verification and the whole test, instead of restarting per phase.

const API_URL = process.env.REACT_APP_BACKEND_URL;

let stream = null;
let fullRecorder = null;
let fullChunks = [];
let chunkIndex = 0;
let uploadInterval = null;
let sessionId = null;
let mimeType = 'video/webm';
let recording = false;

export function getSharedStream() {
  return stream && stream.active ? stream : null;
}

export async function acquireSharedCamera({ audio = false } = {}) {
  if (stream && stream.active) return stream;
  stream = await navigator.mediaDevices.getUserMedia({ video: true, audio });
  return stream;
}

export function isSharedRecording() {
  return recording;
}

export function startSharedFullRecording(sid) {
  if (recording || !stream) return;
  sessionId = sid;
  try {
    mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
      ? 'video/webm;codecs=vp8'
      : 'video/webm';
    fullRecorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 300000 });
    fullChunks = [];
    chunkIndex = 0;
    fullRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) fullChunks.push(e.data);
    };
    fullRecorder.start(5000);
    recording = true;
    uploadInterval = setInterval(() => uploadChunk(false), 60000);
    console.log('Shared continuous recording started at camera grant');
  } catch (err) {
    console.error('Failed to start shared full recording:', err);
  }
}

async function uploadChunk(isFinal) {
  if (!sessionId) return;
  if (fullChunks.length === 0) return;
  const chunks = [...fullChunks];
  fullChunks = [];
  const blob = new Blob(chunks, { type: mimeType });
  if (blob.size < 5000 && !isFinal) return;

  const token = localStorage.getItem('token');
  const idx = chunkIndex;
  chunkIndex += 1;

  await new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = async () => {
      try {
        const b64 = reader.result.split(',')[1];
        const formData = new FormData();
        formData.append('session_id', sessionId);
        formData.append('video_base64', b64);
        formData.append('chunk_index', idx);
        formData.append('is_final', isFinal);
        formData.append('mime_type', mimeType);
        await fetch(`${API_URL}/api/proctoring/upload-full-recording`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        console.log(`Shared recording chunk ${idx} uploaded (final=${isFinal})`);
      } catch (err) {
        console.error('Failed to upload shared recording chunk:', err);
      } finally {
        resolve();
      }
    };
    reader.onerror = () => resolve();
  });
}

export async function stopSharedFullRecording() {
  if (uploadInterval) {
    clearInterval(uploadInterval);
    uploadInterval = null;
  }
  if (fullRecorder && fullRecorder.state !== 'inactive') {
    try { fullRecorder.requestData(); } catch (e) { /* noop */ }
    try { fullRecorder.stop(); } catch (e) { /* noop */ }
    await new Promise((r) => setTimeout(r, 400));
  }
  await uploadChunk(true);
  recording = false;
  fullRecorder = null;
}

export function stopSharedStream() {
  if (stream) {
    stream.getTracks().forEach((t) => t.stop());
    stream = null;
  }
  recording = false;
  sessionId = null;
}
